import { DateTime } from 'luxon';
import dayjs from 'dayjs';

export default class DateTimeUtils {
  /**
   * Parses a raw date string into a valid Date.
   * Supports date-only values (YYYY-MM-DD) by normalizing them to local midnight.
   */
  static parseDateInput(rawDate?: string | null): Date | null {
    if (!rawDate) {
      return null;
    }
    const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(rawDate)
      ? new Date(`${rawDate}T00:00:00`)
      : new Date(rawDate);
    if (isNaN(parsedDate.getTime())) {
      return null;
    }
    return parsedDate;
  }

  /**
   * Normalizes any valid Date/string input to YYYY-MM-DD.
   */
  static normalizeDateOnly(dateValue?: Date | string | null): string | null {
    if (!dateValue) {
      return null;
    }
    const parsedDate = dayjs(dateValue);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format('YYYY-MM-DD');
  }
  /**
   * Calculates the departure time by adding the specified duration (in minutes) to the given arrival time.
   * @param {string} arrivalTime - The arrival time in ISO string format.
   * @param {number} duration - The duration in minutes to add to the arrival time.
   * @returns {string} The departure time in ISO string format.
   */
  calculateDepartureTime(arrivalTime: string, duration: number): string {
    try {
      // Parse the arrival time in UTC
      const arrivalDateTime = DateTime.fromISO(arrivalTime, { zone: 'utc' });

      if (!arrivalDateTime.isValid) {
        throw new Error('Invalid arrivalTime format. Expected an ISO string.');
      }

      // Add duration while keeping the same timezone
      const departureDateTime = arrivalDateTime.plus({ minutes: duration });

      // Return in ISO format with UTC timezone
      return departureDateTime.toUTC().toISO();
    } catch (error) {
      console.error('Error calculating departure time:', error);
      throw error;
    }
  }

  /**
   * Formats a JavaScript Date object into a string in ISO 8601 format.
   * @param {Date} date - The date object to format.
   * @returns {string} The formatted date-time string in ISO 8601 format.
   */
  formatDate(date: Date): string {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      throw new Error('Invalid date object provided.');
    }

    return date.toISOString();
  }
}
